/** Review page · "Conduct observations" — the interview's conduct signals in one highlighted card,
 *  placed before the transcript. Two sources, always labelled apart:
 *   - Detected: what the candidate's browser/camera recorded (tab switches, pastes, leaving full-screen,
 *     a second face, an empty frame). Counted and timed; never a score.
 *   - Recruiter-added: what a person watching the recording noticed that a browser cannot (reading from
 *     notes, someone prompting off camera). Editable by the recruiter; every entry shows who wrote it.
 *  Advisory context for the human decision — nothing here scores, ranks or rejects. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OBSERVATION_CATEGORIES } from "@talenthop/shared";
import type { IntegrityEventKind, ObservationCategory } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { INTEGRITY_EVENT_LABEL } from "./integrityLabels.js";
import { useReportSource } from "./reportSource.js";

/** Detected events worth a card. Gaze is deliberately absent: it has its own block with the fairness
 *  caveat attached (see integrityLabels.ts), and repeating it here without the caveat would turn
 *  context into accusation. */
const DETECTED: { kind: IntegrityEventKind; noun: string }[] = [
  { kind: "tab_blur", noun: "Left the interview tab" },
  { kind: "paste", noun: "Pasted content" },
  { kind: "fullscreen_exit", noun: "Left full-screen" },
  { kind: "second_face", noun: "Second face in frame" },
  { kind: "no_face", noun: "Face not visible" },
];

const CATEGORY_LABEL: Record<ObservationCategory, string> = {
  reading_script: "Appeared to read from a script or notes",
  another_person: "Another person prompting or answering",
  another_screen: "Looking at another screen or tab",
  tech_issue: "Likely a technical issue",
  other: "Other",
};

interface Observation {
  id: string;
  authorName: string | null;
  category: ObservationCategory;
  note: string;
  atSeconds: number | null;
  createdAt: string;
}

const mmss = (s: number): string => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** "1:05" or "65" → seconds; blank → undefined; anything else → null (invalid). */
function parseAt(v: string): number | undefined | null {
  const t = v.trim();
  if (!t) return undefined;
  const m = /^(\d{1,3}):([0-5]?\d)$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  return /^\d{1,5}$/.test(t) ? Number(t) : null;
}

export function ConductObservations({
  candidacyId,
  events,
  captured,
  onSeek,
}: {
  candidacyId: string;
  events: { kind: IntegrityEventKind; at: string; detail: string | null }[];
  /** True when the browser sent any integrity heartbeat — otherwise "none detected" would be a false all-clear. */
  captured: boolean;
  /** Jump the recording to a time the recruiter typed. */
  onSeek?: (sec: number) => void;
}): ReactNode {
  const qc = useQueryClient();
  // On a shared report: the share-scoped read, and no add / remove.
  const { shareToken, readOnly } = useReportSource();
  const key = ["observations", shareToken ?? candidacyId];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<{ observations: Observation[] }>(
        shareToken ? `/shared/${shareToken}/observations` : `/candidacies/${candidacyId}/observations`,
      ),
    enabled: candidacyId.length > 0 || !!shareToken,
    retry: shareToken ? false : undefined,
  });
  const [adding, setAdding] = useState(false);
  const [category, setCategory] = useState<ObservationCategory>("reading_script");
  const [note, setNote] = useState("");
  const [at, setAt] = useState("");
  const atParsed = parseAt(at);

  const add = useMutation({
    mutationFn: () =>
      api.post(`/candidacies/${candidacyId}/observations`, {
        category,
        note: note.trim(),
        ...(typeof atParsed === "number" ? { atSeconds: atParsed } : {}),
      }),
    onSuccess: () => {
      setNote("");
      setAt("");
      setAdding(false);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/candidacies/${candidacyId}/observations/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  const count = (k: IntegrityEventKind): number => events.filter((e) => e.kind === k).length;
  const cards = DETECTED.map((d) => ({ ...d, n: count(d.kind) }));
  const flagged = cards.filter((c) => c.n > 0);
  const observations = q.data?.observations ?? [];
  const detectedEvents = events.filter((e) => DETECTED.some((d) => d.kind === e.kind || (d.kind === "tab_blur" && e.kind === "tab_focus")));

  return (
    <div className="card t1-rv-conduct">
      <div className="card-h">
        <h3>Conduct observations</h3>
        <span className="mut sm">advisory — for your review, not a score</span>
      </div>
      <div className="card-b">
        <div className="t1-rv-conduct-cards">
          {cards.map((c) => (
            <div key={c.kind} className={`t1-rv-conduct-card${c.n > 0 ? " flag" : ""}`}>
              <div className="n tnum">{captured || c.n > 0 ? c.n : "—"}</div>
              <div className="l">{c.noun}</div>
            </div>
          ))}
          <div className={`t1-rv-conduct-card${observations.length > 0 ? " flag" : ""}`}>
            <div className="n tnum">{observations.length}</div>
            <div className="l">{readOnly ? "Added by the hiring team" : "Added by your team"}</div>
          </div>
        </div>
        <p className="mut sm" style={{ margin: "10px 0 0" }}>
          {!captured && flagged.length === 0
            ? "The candidate's browser sent no integrity signals for this interview, so “none” here would not be proof of anything."
            : flagged.length === 0
              ? "Nothing unusual was detected by the browser or camera. It cannot see a second screen or hear someone off camera — if the recording made you doubt, add what you noticed below."
              : "Detected by the candidate's browser and camera. A tab switch can be innocent (a notification, a technical issue) — weigh it against the recording."}
        </p>

        {detectedEvents.length > 0 && (
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
              Detected timeline ({detectedEvents.length})
            </summary>
            <div style={{ marginTop: 8 }}>
              {detectedEvents.map((e, i) => (
                <div key={i} className="krow">
                  <span className="k">
                    <span className="pill n">{INTEGRITY_EVENT_LABEL[e.kind] ?? e.kind}</span>
                  </span>
                  <span className="v mut sm">{new Date(e.at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        <div className="t1-rv-conduct-mine">
          <div className="t1-rv-conduct-mine-h">
            <b>{readOnly ? "Hiring team's observations" : "Your team's observations"}</b>
            {!adding && !readOnly && (
              <button type="button" className="btn sm" onClick={() => setAdding(true)}>
                + Add observation
              </button>
            )}
          </div>
          {observations.length === 0 && !adding && readOnly && (
            <p className="mut sm" style={{ margin: "6px 0 0" }}>None recorded.</p>
          )}
          {observations.length === 0 && !adding && !readOnly && (
            <p className="mut sm" style={{ margin: "6px 0 0" }}>
              Doubt something the system couldn't catch — reading from notes, someone helping off camera? Record it here
              so the rest of the team sees it before deciding.
            </p>
          )}
          {observations.map((o) => (
            <div key={o.id} className="t1-rv-conduct-obs">
              <div style={{ minWidth: 0 }}>
                <span className="pill r">{CATEGORY_LABEL[o.category]}</span>
                {o.atSeconds != null && (
                  <button
                    type="button"
                    className="btn sm ghost"
                    style={{ marginLeft: 6 }}
                    disabled={!onSeek}
                    onClick={() => onSeek?.(o.atSeconds!)}
                    title="Jump the recording to this moment"
                  >
                    ▶ {mmss(o.atSeconds)}
                  </button>
                )}
                <p style={{ margin: "6px 0 2px", overflowWrap: "anywhere" }}>{o.note}</p>
                <span className="mut sm">
                  Recruiter observation · {o.authorName ?? "A teammate"} · {new Date(o.createdAt).toLocaleDateString()}
                </span>
              </div>
              {!readOnly && <button
                type="button"
                className="btn sm ghost"
                aria-label="Remove observation"
                disabled={remove.isPending}
                onClick={() => remove.mutate(o.id)}
              >
                Remove
              </button>}
            </div>
          ))}
          {adding && (
            <form
              className="t1-rv-conduct-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (note.trim() && atParsed !== null) add.mutate();
              }}
            >
              <label>
                <span className="mut sm">What did you notice?</span>
                <select value={category} onChange={(e) => setCategory(e.target.value as ObservationCategory)}>
                  {OBSERVATION_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABEL[c]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="mut sm">Details</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="e.g. Eyes track left to right in Q3 as if reading; a second voice is audible at 2:10."
                  required
                />
              </label>
              <label>
                <span className="mut sm">Moment in the recording (optional, e.g. 2:10)</span>
                <input value={at} onChange={(e) => setAt(e.target.value)} placeholder="m:ss" inputMode="numeric" style={{ maxWidth: 120 }} />
                {atParsed === null && <span className="sm" style={{ color: "var(--crit)" }}>Use m:ss or seconds.</span>}
              </label>
              {add.isError && <p className="sm" style={{ color: "var(--crit)", margin: 0 }}>Couldn't save — please try again.</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" className="btn sm primary" disabled={!note.trim() || atParsed === null || add.isPending}>
                  Save observation
                </button>
                <button type="button" className="btn sm ghost" onClick={() => (setAdding(false), add.reset())}>
                  Cancel
                </button>
              </div>
              <p className="mut sm" style={{ margin: 0 }}>
                Saved as your observation with your name. It's context for the decision — it doesn't change any score.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
